/**
 * Composition: create adapters and use cases, wire dependencies.
 */

import { getLocale, setLocale, t } from "./i18n/index.js";
import { createLocalStorageAdapter } from "./adapters/storage/local-storage-adapter.js";
import { createCanvasRenderAdapter } from "./adapters/render/canvas-render-adapter.js";
import { createHudDomAdapter } from "./adapters/hud/hud-dom-adapter.js";
import { createWebAudioAdapter } from "./adapters/audio/web-audio-adapter.js";
import { createWebMusicAdapter } from "./adapters/audio/web-music-adapter.js";
import { createFieldConfigAdapter } from "./adapters/data/field-config-adapter.js";
import { createGameLoop } from "./adapters/input/game-loop.js";
import { createInputAdapter } from "./adapters/input/input-adapter.js";
import { createLoadProgress } from "./application/use-cases/load-progress.js";
import { createGoToMap } from "./application/use-cases/go-to-map.js";
import { createExitToMap } from "./application/use-cases/exit-to-map.js";
import { createStartBattle } from "./application/use-cases/start-battle.js";
import { createPlaceBuilding } from "./application/use-cases/place-building.js";
import { createCreateGem } from "./application/use-cases/create-gem.js";
import { createPlaceGem } from "./application/use-cases/place-gem.js";
import { createRemoveGemFromTower } from "./application/use-cases/remove-gem-from-tower.js";
import { createSwapGemsBetweenTowers } from "./application/use-cases/swap-gems-between-towers.js";
import { sessionToView, mapStateToView, storyToView } from "./application/state-view.js";
import { createUpgradeGem } from "./application/use-cases/upgrade-gem.js";
import { createDuplicateGem } from "./application/use-cases/duplicate-gem.js";
import { createCombineGems } from "./application/use-cases/combine-gems.js";
import { createRecycleGem } from "./application/use-cases/recycle-gem.js";
import { createTick } from "./application/use-cases/tick.js";
import { createSaveProgress } from "./application/use-cases/save-progress.js";
import { createShowStory } from "./application/use-cases/show-story.js";

import {
  createPause,
  createResume,
  createSetSpeed,
  createCycleSpeed,
} from "./application/use-cases/pause-resume.js";
import { createSummonWave } from "./application/use-cases/summon-wave.js";
import { createLearnSkill, createRefundSkill } from "./application/use-cases/learn-skill.js";
import { createOpenBattleSettings } from "./application/use-cases/open-battle-settings.js";
import { createUpdateBattleSettings } from "./application/use-cases/update-battle-settings.js";
import { createConfirmBattleSettings } from "./application/use-cases/confirm-battle-settings.js";
import { createCancelBattleSettings } from "./application/use-cases/cancel-battle-settings.js";
import { createDemolishTower } from "./application/use-cases/demolish-tower.js";
import { createCastGemBomb } from "./application/use-cases/cast-gem-bomb.js";
import type { GameSession } from "./domain/session.js";
import type { AppState } from "./application/types.js";
import type { MapProgress } from "./domain/map-progress.js";
import { completeField, createInitialMapProgress } from "./domain/map-progress.js";
import { activeCampaign, type CampaignConfig } from "./campaign/index.js";
import {
  type MageProfile,
  createMageProfile,
  awardStageXp,
  incrementStatKills,
  incrementStatBanishes,
  addStatPlayTime,
  recordStageVictory,
  recordStageFailure,
  incrementStatBattles,
  incrementStatEarlyWaveHurryMana,
  incrementStatEarlyWaveHurries,
  availableSkillPoints,
  SKILL_SPECS,
} from "./domain/mage-profile.js";
import { checkNewAchievements, ACHIEVEMENTS } from "./domain/achievements.js";
import { levelFromXp } from "./domain/mage-xp.js";
import { type StageSettings, createDefaultStageSettings } from "./domain/stage-settings.js";
import { createEditor } from "./application/use-cases/editor.js";
import { computeFullPath } from "./domain/pathfinding.js";
import { createFieldOverrideStorage } from "./adapters/storage/field-override-adapter.js";
import type { AudioPort, GameStateView, MusicPort } from "./application/ports.js";
import type { SceneId } from "./domain/types.js";
import { setI18nCampaign } from "./i18n/index.js";

export interface CompositionOptions {
  mountTarget: HTMLElement;
  campaign?: CampaignConfig;
}

// ----------------------------------------------------------------
//  Tutorial state (module-level, persisted via localStorage)
// ----------------------------------------------------------------
let tutorialStep: number = 0; // -1 = done, 0-4 = active step index

function initTutorial(campaign: CampaignConfig = activeCampaign): void {
  if (typeof localStorage !== "undefined" && localStorage.getItem(campaign.tutorialStorageKey) === "1") {
    tutorialStep = -1;
  } else {
    tutorialStep = 0;
  }
}

function advanceTutorial(
  trigger: "build" | "relic" | "equip" | "start" | "demolish" | "manual",
  renderPort?: { setTutorialStep(step: number): void },
  campaign: CampaignConfig = activeCampaign
): void {
  if (tutorialStep < 0) return;
  const stepTriggerMap = ["relic", "equip", "start", "demolish"];
  if (trigger !== "manual" && stepTriggerMap[tutorialStep] !== trigger) return;
  tutorialStep += 1;
  if (tutorialStep >= 4) {
    tutorialStep = -1;
    if (typeof localStorage !== "undefined") localStorage.setItem(campaign.tutorialStorageKey, "1");
  }
  renderPort?.setTutorialStep(tutorialStep);
}

function dismissTutorial(
  renderPort?: { setTutorialStep(step: number): void },
  campaign: CampaignConfig = activeCampaign
): void {
  tutorialStep = -1;
  if (typeof localStorage !== "undefined") localStorage.setItem(campaign.tutorialStorageKey, "1");
  renderPort?.setTutorialStep(-1);
}

let currentSession: GameSession | null = null;
let lastAppState: AppState | null = null;
let currentMapProgress: MapProgress = createInitialMapProgress(activeCampaign.map.startingFieldId);
let currentMageProfile: MageProfile = createMageProfile();
let skillsPanelOpen = false;
let statsPanelOpen = false;
let pendingBattleFieldId: string | null = null;
let stageSettingsDraft: StageSettings = createDefaultStageSettings();
let currentSceneOverride: "map" | "settings" | "editor" | "story" | null = null;
let currentAppState: AppState = { scene: "map", mapProgress: currentMapProgress };


function getSession(): GameSession | null {
  return currentSession;
}

/**
 * Build authored-YAML text for the given editor state + source config.
 * Used by the in-editor "📋 Экспорт YAML" action.
 */
function buildFieldYaml(
  state: import("./application/use-cases/editor.js").EditorState,
  config: import("./domain/types.js").FieldConfig
): string {
  const blockedSet = new Set(state.blockedCells.map((c) => `${c.row},${c.col}`));
  const pathCells = computeFullPath(state.rows, state.cols, state.waypoints, blockedSet);
  const pathSet = new Set(pathCells.map((c) => `${c.row},${c.col}`));
  const entry = state.waypoints[0];
  const exit = state.waypoints.length >= 2 ? state.waypoints[state.waypoints.length - 1] : null;

  const rows: string[] = [];
  for (let r = 0; r < state.rows; r++) {
    const glyphs: string[] = [];
    for (let c = 0; c < state.cols; c++) {
      const key = `${r},${c}`;
      let g: string;
      if (entry && r === entry.row && c === entry.col) g = "<";
      else if (exit && r === exit.row && c === exit.col) g = ">";
      else if (pathSet.has(key)) g = "p";
      else if (blockedSet.has(key)) g = "#";
      else g = ".";
      glyphs.push(g);
    }
    rows.push("  " + glyphs.join(" "));
  }

  const lines = [
    `initialMana: ${config.initialMana ?? 600}`,
    `wavesNum: ${state.wavesNum}`,
    `monsterBaseHp: ${state.monsterBaseHp}`,
    `armorGrowthRate: ${config.armorGrowthRate ?? 0.11}`,
    `firstWaveDelayMs: ${config.firstWaveDelayMs ?? 4000}`,
    `interWaveDelayMs: ${config.interWaveDelayMs ?? 3500}`,
    `monstersPerWave: ${state.monstersPerWave}`,
    `spawnIntervalMs: ${config.spawnIntervalMs ?? 1200}`,
    `grid: |`,
    ...rows,
  ];
  return lines.join("\n") + "\n";
}

function setSession(session: GameSession | null): void {
  currentSession = session;
}

function getMapProgress(): MapProgress | undefined {
  return currentMapProgress;
}

function setMapProgress(mp: MapProgress): void {
  currentMapProgress = mp;
  if (lastAppState) lastAppState.mapProgress = mp;
}

function setMageProfile(profile: MageProfile): void {
  currentMageProfile = profile;
}

function getState(): AppState {
  return currentAppState;
}

function setState(state: AppState): void {
  currentAppState = state;
  lastAppState = state;
}


function getPendingFieldId(): string | null {
  return pendingBattleFieldId;
}

function setPendingFieldId(id: string | null): void {
  pendingBattleFieldId = id;
}

function getDraftSettings(): StageSettings {
  return stageSettingsDraft;
}

function setDraftSettings(s: StageSettings): void {
  stageSettingsDraft = s;
}

export function createComposition(options: CompositionOptions) {
  const campaign = options.campaign ?? activeCampaign;
  setI18nCampaign(campaign.id as Parameters<typeof setI18nCampaign>[0]);
  document.title = campaign.title;

  currentSession = null;
  lastAppState = null;
  currentMapProgress = createInitialMapProgress(campaign.map.startingFieldId);
  currentMageProfile = createMageProfile();
  skillsPanelOpen = false;
  statsPanelOpen = false;
  pendingBattleFieldId = null;
  stageSettingsDraft = createDefaultStageSettings();
  currentSceneOverride = null;
  currentAppState = { scene: "map", mapProgress: currentMapProgress };

  const storage = createLocalStorageAdapter(campaign);
  const canvas = document.createElement("canvas");
  canvas.width = 1280;
  canvas.height = 720;
  // Insert canvas into #canvas-wrapper if available (normal runtime); fallback to mountTarget (tests).
  const canvasWrapper = options.mountTarget.querySelector<HTMLElement>("#canvas-wrapper");
  const canvasParent = canvasWrapper ?? options.mountTarget;
  canvasParent.insertBefore(canvas, canvasParent.firstChild);
  const render = createCanvasRenderAdapter({
    mountTarget: options.mountTarget,
    canvas,
    width: 1280,
    height: 720,
    campaign,
  });

  // Phase 2: HUD DOM adapter with callbacks wired after use-cases are created.
  // We use late-bound callbacks so speed control, placement mode etc can be
  // referenced via closure after their creation below.
  const hudRoot = options.mountTarget.querySelector<HTMLElement>("#hud-root") ?? canvasParent;
  const hud = createHudDomAdapter({
    mountTarget: hudRoot,
    onSpeedChange: (speed) => setSpeedLate(speed),
    onPlacementModeChange: (mode) => setPlacementModeLate(mode),
    onWaveSummon: (waveIndex) => summonWaveLate(waveIndex),
    // Top-bar ⚙ click is intercepted by hud-dom-adapter to open the battle
     // menu; this callback is retained for diagnostic logging only.
    onSettings: () => {},
    onBattleMenuOpen: () => onBattleMenuOpenLate(),
    onBattleMenuClose: () => onBattleMenuCloseLate(),
    onBattleMenuToggleAudio: () => onBattleMenuToggleAudioLate(),
    onBattleMenuToggleLocale: () => onBattleMenuToggleLocaleLate(),
    onBattleMenuExit: () => handleExitToMapLate(),
    onBattleMenuRestart: () => onBattleMenuRestartLate(),
    isAudioMuted: () => (audio as AudioPort & { isMuted?(): boolean }).isMuted?.() ?? false,
    onSanctify: () => upgradeGemLate(),
    onReplicate: () => duplicateGemLate(),
    onDuplicate: () => duplicateGemLate(),
    onCombine: () => combineGemsLate(),
    onMeltdown: () => recycleGemLate(),
    onGemCreate: (gemType) => createGemLate(gemType),
    onInventoryDragStart: (params) => inventoryDragStartLate(params),
    onInventoryGemClick: (gemId) => inventoryGemClickLate(gemId),
    onInventoryGemDrop: (sourceGemId, targetGemId) => inputAdapterRef?.combineInventoryGems(sourceGemId, targetGemId),
    // Phase 3: settings scene callbacks
    onHpChange: (index) => onHpChangeLate(index),
    onWavesChange: (index) => onWavesChangeLate(index),
    onToggleChange: (key) => onToggleChangeLate(key),
    onSettingsStart: () => onSettingsStartLate(),
    onSettingsCancel: () => onSettingsCancelLate(),
    // Phase 3: tutorial callbacks
    onTutorialNext: () => advanceTutorial("manual", renderAndHud, campaign),
    onTutorialSkip: () => dismissTutorial(renderAndHud, campaign),
    // Phase 3: summary callbacks
    onReturnToMap: () => handleExitToMapLate(),
    // Phase 3: skills callbacks
    onSkillUpgrade: (skillId) => onSkillUpgradeLate(skillId),
    onSkillRefund: (skillId, options) => onSkillRefundLate(skillId, options),
    onSkillsPanelClose: () => closeSkillsPanelLate(),
    onSkillsPanelToggle: () => toggleSkillsPanelLate(),
    // Phase 3: stats callbacks
    onStatsPanelClose: () => closeStatsPanelLate(),
    onStatsPanelToggle: () => toggleStatsPanelLate(),
    // Map CTA
    onFieldLaunch: (fieldId) => openBattleSettingsLate(fieldId),
    onFieldEdit: (fieldId) => {
      const progress = getMapProgress();
      if (progress && !progress.unlockedFieldIds.has(fieldId)) return;
      currentSceneOverride = "editor";
      editorUCRef?.openEditor(fieldId);
    },
    // Editor panel callbacks
    onEditorSave: () => { currentSceneOverride = null; editorUCRef?.closeEditor(true); },
    onEditorCancel: () => { currentSceneOverride = null; editorUCRef?.closeEditor(false); },
    onEditorResetOverride: () => {
      const st = editorUCRef?.getEditorState();
      if (!st) return;
      fieldOverrideStorage.clearOverride(st.fieldId);
      fieldConfig.invalidate?.(st.fieldId);
      currentSceneOverride = null;
      editorUCRef?.closeEditor(false);
    },
    onEditorClearWaypoints: () => editorUCRef?.editorClearWaypoints(),
    onEditorRemoveLastWaypoint: () => editorUCRef?.editorRemoveLastWaypoint(),
    onEditorSetMode: (mode) => editorUCRef?.editorSetMode(mode),
    onEditorSetWavesNum: (n) => editorUCRef?.editorSetWavesNum(n),
    onEditorSetMonstersPerWave: (n) => editorUCRef?.editorSetMonstersPerWave(n),
    onEditorSetMonsterBaseHp: (n) => editorUCRef?.editorSetMonsterBaseHp(n),
    onEditorExportYaml: () => onEditorExportYamlLate(),
  });
  hud.mount(hudRoot);

  // Late-bound stubs updated after use-case wiring
  let setSpeedLate: (speed: 0 | 1 | 3) => void = () => {};
  let setPlacementModeLate: (mode: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine") => void = () => {};
  let upgradeGemLate: () => void = () => {};
  let duplicateGemLate: () => void = () => {};
  let combineGemsLate: () => void = () => {};
  let recycleGemLate: () => void = () => {};
  let inventoryDragStartLate: (p: { gemId: string; screenX: number; screenY: number }) => void = () => {};
  let inventoryGemClickLate: (gemId: string) => void = () => {};
  // Phase 3 late-bound stubs
  let onHpChangeLate: (index: number) => void = () => {};
  let onWavesChangeLate: (index: number) => void = () => {};
  let onToggleChangeLate: (key: "extraBanishCost" | "healOnReturn") => void = () => {};
  let onSettingsStartLate: () => void = () => {};
  let onSettingsCancelLate: () => void = () => {};
  let handleExitToMapLate: () => void = () => {};
  let onSkillUpgradeLate: (skillId: number, options?: { all?: boolean }) => void = () => {};
  let onSkillRefundLate: (skillId: number, options?: { all?: boolean }) => void = () => {};
  let closeSkillsPanelLate: () => void = () => {};
  let toggleSkillsPanelLate: () => void = () => {};
  let closeStatsPanelLate: () => void = () => {};
  let toggleStatsPanelLate: () => void = () => {};
  let openBattleSettingsLate: (fieldId: string) => void = () => {};
  let summonWaveLate: (waveIndex: number) => void = () => {};
  let createGemLate: (gemType: string) => void = () => {};
  let onBattleMenuOpenLate: () => void = () => {};
  let onBattleMenuCloseLate: () => void = () => {};
  let onBattleMenuToggleAudioLate: () => void = () => {};
  let onBattleMenuToggleLocaleLate: () => void = () => {};
  let onBattleMenuRestartLate: () => void = () => {};
  let onEditorExportYamlLate: () => void = () => {};

  /**
   * Scene currently shown by render/HUD (authoritative for input).
   * Must not be inferred from getSession(): after LoadProgress the UI can be
   * the map while an in-progress battle session is still held in memory.
   */
  let lastRenderedScene: SceneId = "map";

  function campaignUiView() {
    const ui = campaign.ui;
    return {
      brandTitle: ui.brandTitle,
      brandIcon: ui.brandIcon,
      mageIcon: ui.mageIcon,
      mageLabelKey: ui.mageLabelKey,
      resourceLabelKey: ui.resourceLabelKey,
      waveTypeCodes: { ...ui.waveTypeCodes },
    };
  }

  function applyCampaignToView(state: GameStateView): GameStateView {
    const campaignUi = campaignUiView();
    if (state.scene === "map") {
      return {
        ...state,
        campaignUi,
        map: state.map
          ? { ...state.map, fieldIds: [...campaign.map.fieldIds] }
          : state.map,
      };
    }
    if (state.scene === "story" && state.story) {
      const beatId = state.story.beatId;
      return {
        ...state,
        campaignUi,
        story: {
          beatId,
          panels: (campaign.story.beats[beatId] ?? []).map((panel) => ({
            imageSrc: panel.imageSrc,
            text: t(panel.textKey),
          })),
          currentPanelIndex: state.story.currentPanelIndex,
        },
      };
    }
    return { ...state, campaignUi };
  }

  // Thin render wrapper: forwards all calls to canvas adapter; additionally
  // calls hud.update() on every state update so HUD stays in sync.
  // For game-scene updates we always stamp the current placement mode from
  // inputAdapterRef onto the state before forwarding to the HUD. This prevents
  // tick / use-cases that call sessionToView without an explicit mode from
  // resetting the DOM highlight back to "Tower" on every tick.
  const renderAndHud: typeof render = new Proxy(render, {
    get(target, prop, receiver) {
      if (prop === "setScene") {
        return (sceneId: SceneId) => {
          lastRenderedScene = sceneId;
          currentAppState = { ...currentAppState, scene: sceneId };
          target.setScene(sceneId);
          applyMusicForScene(sceneId);
        };
      }
      if (prop === "update") {
        return (state: import("./application/ports.js").GameStateView) => {
          // Always read placement mode and tutorial step from composition (authoritative).
          const patchedState =
            state.scene === "game"
              ? {
                  ...state,
                  placementMode: inputAdapterRef?.getPlacementMode() ?? ("Tower" as const),
                  tutorialStep,
                }
              : state;
          const campaignState = applyCampaignToView(patchedState);
          lastRenderedScene = campaignState.scene;
          target.update(campaignState);
          hud.update(campaignState);
          // Legacy static top-bar "To map" button: only makes sense in battle.
          const btn = options.mountTarget.querySelector<HTMLButtonElement>("#go-to-map-btn");
          if (btn) btn.style.display = campaignState.scene === "game" ? "" : "none";
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  });

  const audio = createWebAudioAdapter();
  const music = createWebMusicAdapter(campaign.audio.tracks);
  const fieldOverrideStorage = createFieldOverrideStorage(campaign.id);
  const fieldConfig = createFieldConfigAdapter(fieldOverrideStorage, campaign);
  let lastSceneForMusic: import("./domain/types.js").SceneId | null = null;
  let winThemePlaying = false;
  function applyMusicForScene(scene: import("./domain/types.js").SceneId): void {
    if (scene === lastSceneForMusic) return;
    lastSceneForMusic = scene;
    if (scene === "map") {
      // Returning to map after a win: let wintheme finish naturally; otherwise play map theme.
      if (winThemePlaying) return;
      music.playMusic("map", { loop: true });
    } else if (scene === "game") {
      winThemePlaying = false;
      music.playMusic("battle", { loop: true });
    }
    // For "settings" / "menu": keep whatever was playing.
  }

  function getMageProfile(): MageProfile {
    return currentMageProfile;
  }

  const depsSave = { storage, getSession, getMapProgress, getMageProfile };

  /** Pops toast notifications on newly-unlocked achievements. */
  function announceAchievements(newIds: string[]): void {
    if (!newIds.length) return;
    for (const id of newIds) {
      const spec = ACHIEVEMENTS.find((a) => a.id === id);
      if (!spec) continue;
      renderAndHud.showAchievementToast(t(spec.name), t(spec.description));
    }
  }
  const saveProgress = createSaveProgress(depsSave);

  let pendingStoryBeat: string | null = null;
  // If set, invoked by storyActions.onFinish instead of the default goToMap().
  // Used to route story beats that happen on won-screen→map exit through the
  // full exitToMap + gameLoop.stop path.
  let storyFinishFn: (() => void) | null = null;

  const handleWin = (fieldId: string): void => {
    completeField(currentMapProgress, fieldId, campaign.map.getAdjacentFieldIds);
    if (lastAppState) lastAppState.mapProgress = currentMapProgress;
    saveProgress();
    winThemePlaying = true;
    music.playMusic("win", { loop: false });

    // Queue story beat to show on the won→map transition (not immediately,
    // so the won-screen renders correctly first).
    const fieldStoryBeat = campaign.story.fieldWinBeats[fieldId];
    if (fieldStoryBeat && !currentMapProgress.seenStoryBeats.has(fieldStoryBeat)) {
      pendingStoryBeat = fieldStoryBeat;
    }
  };


  const handleStageWon = (fieldId: string, earnedXp: number): void => {
    const levelBefore = levelFromXp(currentMageProfile.totalXp);
    awardStageXp(currentMageProfile, fieldId, earnedXp);
    const levelAfter = levelFromXp(currentMageProfile.totalXp);
    if (levelAfter > levelBefore) {
      renderAndHud.showAchievementToast(
        t("toast.levelUp", { level: levelAfter }),
        t("toast.levelUpSubtitle")
      );
      const newlyUnlocked = SKILL_SPECS.filter(
        (skill) => skill.playerLevelReq > levelBefore && skill.playerLevelReq <= levelAfter
      );
      const points = availableSkillPoints(currentMageProfile, levelAfter);
      if (newlyUnlocked.length === 1) {
        renderAndHud.showAchievementToast(
          t("toast.giftsUnlocked"),
          t("toast.giftUnlockedSubtitle", {
            name: t(`skill.${newlyUnlocked[0].id}`),
            points,
          })
        );
      } else if (newlyUnlocked.length > 1) {
        renderAndHud.showAchievementToast(
          t("toast.giftsUnlocked"),
          t("toast.giftsUnlockedSubtitle", { count: newlyUnlocked.length, points })
        );
      } else if (points > 0) {
        renderAndHud.showAchievementToast(
          t("toast.giftsAvailable"),
          t("toast.giftsAvailableSubtitle", { points })
        );
      }
    }
    // saveProgress will persist mageProfile via depsSave when called after handleWin
  };

  const loadProgress = createLoadProgress({
    storage,
    render: renderAndHud,
    fieldConfig,
    startingFieldId: campaign.map.startingFieldId,
    setSession,
    setMapProgress,
    setMageProfile,
  });

  const _goToMapUC = createGoToMap({ render: renderAndHud, getState, getMageProfile });

  // Wrapper: if a story beat is queued (set by handleWin), show it before going
  // to the map. The story's onFinish clears the override and calls goToMap again
  // (at which point pendingStoryBeat is null, so the map renders normally).
  function goToMap(): void {
    if (pendingStoryBeat !== null) {
      const beat = pendingStoryBeat;
      pendingStoryBeat = null;
      currentSceneOverride = "story";
      storyActions.showStory(beat);
      return;
    }
    _goToMapUC();
  }

  const storyActions = createShowStory({
    render: renderAndHud,
    getState,
    setState,
    getMapProgress: () => currentMapProgress,
    saveProgress,
    onFinish: () => {
      currentSceneOverride = null;
      if (storyFinishFn) {
        const fn = storyFinishFn;
        storyFinishFn = null;
        fn();
        return;
      }
      goToMap();
    },
  });

  function nextStoryPanel(): void {
    const state = getState();
    if (state.scene !== "story" || !state.story) return;

    const panelsCount = campaign.story.beats[state.story.beatId]?.length ?? 0;
    const nextIdx = state.story.panelIndex + 1;
    if (nextIdx < panelsCount) {
      const nextState: AppState = {
        ...state,
        scene: "story",
        story: { beatId: state.story.beatId, panelIndex: nextIdx },
      };
      setState(nextState);
      renderAndHud.setScene("story");
      renderAndHud.update(storyToView(nextState, campaign));
      return;
    }

    currentMapProgress.seenStoryBeats.add(state.story.beatId);
    saveProgress();
    currentSceneOverride = null;
    if (storyFinishFn) {
      const fn = storyFinishFn;
      storyFinishFn = null;
      fn();
      return;
    }
    goToMap();
  }

  const startBattle = createStartBattle({
    fieldConfig,
    render: renderAndHud,
    audio,
    setSession,
    getMapProgress,
    getMageProfile,
    incrementBattles: () => {
      incrementStatBattles(currentMageProfile);
      announceAchievements(checkNewAchievements(currentMageProfile));
    },
  });

  const _openBattleSettingsUC = createOpenBattleSettings({
    render: renderAndHud,
    setPendingFieldId,
    setDraftSettings,
    getDraftSettings,
  });
  const openBattleSettings = (fieldId: string): void => {
    const progress = getMapProgress();
    if (progress && !progress.unlockedFieldIds.has(fieldId)) {
      renderAndHud.showAchievementToast(t("hud.fieldLocked"), t("hud.fieldLockedHint"));
      return;
    }
    currentSceneOverride = "settings";
    _openBattleSettingsUC(fieldId);
  };



  const updateBattleSettings = createUpdateBattleSettings({
    render: renderAndHud,
    getDraftSettings,
    setDraftSettings,
    getPendingFieldId,
  });

  const confirmBattleSettings = createConfirmBattleSettings({
    getPendingFieldId,
    getDraftSettings,
    startBattle,
    resetPending: () => { setPendingFieldId(null); setDraftSettings(createDefaultStageSettings()); },
  });

  const _cancelBattleSettingsUC = createCancelBattleSettings({
    render: renderAndHud,
    setDraftSettings,
    setPendingFieldId,
    getState,
    getMageProfile,
  });
  const cancelBattleSettings = (): void => {
    currentSceneOverride = null;
    _cancelBattleSettingsUC();
  };

  // Editor use-case — instantiated here, used later after renderAndHud and fieldConfig are available.
  let editorUCRef: ReturnType<typeof createEditor> | null = null;

  // inputAdapter is created below; we use a late-bound getter so placeBuilding can
  // read the mode that lives inside the input adapter's closure.
  let inputAdapterRef: ReturnType<typeof createInputAdapter> | null = null;

  const depsBuilding = {
    render: renderAndHud,
    audio,
    getSession,
    getPlacementMode: (): "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine" =>
      inputAdapterRef?.getPlacementMode() ?? "Tower",
  };
  const depsGemWithAudio = { render: renderAndHud, audio, getSession };
  const depsGem = { render: renderAndHud, getSession };
  const depsTick = {
    render: renderAndHud,
    audio,
    getSession,
    onWin: handleWin,
    onStageWon: handleStageWon,
    incrementKills: (n: number) => {
      incrementStatKills(currentMageProfile, n);
      announceAchievements(checkNewAchievements(currentMageProfile));
    },
    incrementBanishes: (n: number) => {
      incrementStatBanishes(currentMageProfile, n);
      announceAchievements(checkNewAchievements(currentMageProfile));
    },
    addPlayTime: (ms: number) => addStatPlayTime(currentMageProfile, ms),
    onStageEnd: (fieldId: string, won: boolean) => {
      if (won) recordStageVictory(currentMageProfile, fieldId);
      else recordStageFailure(currentMageProfile, fieldId);
      announceAchievements(checkNewAchievements(currentMageProfile));
      saveProgress();
    },
  };
  const depsPause = { render: renderAndHud, getSession };

  const _placeBuilding = createPlaceBuilding(depsBuilding);
  const _demolishTower = createDemolishTower({ render: renderAndHud, audio, getSession });
  const castGemBomb = createCastGemBomb({ getSession, render: renderAndHud, audio });
  const _createGem = createCreateGem(depsGemWithAudio);
  const _placeGem = createPlaceGem(depsGemWithAudio);
  const removeGemFromTower = createRemoveGemFromTower(depsGemWithAudio);
  const swapGemsBetweenTowers = createSwapGemsBetweenTowers(depsGemWithAudio);
  const upgradeGem = createUpgradeGem(depsGem);
  const duplicateGem = createDuplicateGem(depsGem);
  const combineGems = createCombineGems(depsGem);
  const recycleGem = createRecycleGem(depsGem);
  const tick = createTick(depsTick);
  const pause = createPause(depsPause);
  const resume = createResume(depsPause);
  const cycleSpeed = createCycleSpeed(depsPause);
  const _setSpeed = createSetSpeed(depsPause);

  // Tutorial-aware wrappers
  const placeBuilding: typeof _placeBuilding = (cell, type) => {
    const r = _placeBuilding(cell, type);
    if ("ok" in r && r.ok) {
      advanceTutorial("build", renderAndHud, campaign);
    } else if ("code" in r) {
      const { code } = r;
      if (code === "CellBlocked" || code === "PathBlocked" || code === "CellOccupiedByMonster") {
        // B20: flash the failed cell red instead of a top-level toast
        renderAndHud.flashCell(cell, "rgba(220, 60, 60, 0.55)");
      }
    }
    return r;
  };
  const demolishTower: typeof _demolishTower = (towerId) => {
    const r = _demolishTower(towerId);
    if ("refund" in r) advanceTutorial("demolish", renderAndHud, campaign);
    return r;
  };
  const createGem: typeof _createGem = (gemType) => {
    const r = _createGem(gemType);
    if ("ok" in r && r.ok) advanceTutorial("relic", renderAndHud, campaign);
    return r;
  };
  const placeGem: typeof _placeGem = (gemId, towerId, slotIndex) => {
    const r = _placeGem(gemId, towerId, slotIndex);
    if ("ok" in r && r.ok) advanceTutorial("equip", renderAndHud, campaign);
    return r;
  };
  const setSpeed = (speed: 0 | 1 | 3): void => {
    _setSpeed(speed);
    if (speed !== 0) advanceTutorial("start", renderAndHud, campaign);
  };

  summonWaveLate = createSummonWave({
    render: renderAndHud,
    getSession,
    setSpeed,
    audio,
    onEarlyHurryReward: (mana) => {
      incrementStatEarlyWaveHurryMana(currentMageProfile, mana);
      incrementStatEarlyWaveHurries(currentMageProfile, 1);
      announceAchievements(checkNewAchievements(currentMageProfile));
      renderAndHud.showAchievementToast(
        t("toast.earlyWaveManaTitle", { n: mana }),
        t("toast.earlyWaveManaSubtitle")
      );
    },
  });

  // Wire late-bound HUD callbacks now that use-cases are available
  setSpeedLate = setSpeed;
  setPlacementModeLate = (mode) => {
    inputAdapterRef?.setPlacementMode(mode);
  };
  upgradeGemLate = () => {
    const towerId = inputAdapterRef?.getSelectedTowerId();
    if (!towerId) return;
    upgradeGem({ towerId, slotIndex: 0 });
  };
  duplicateGemLate = () => {
    const towerId = inputAdapterRef?.getSelectedTowerId();
    if (!towerId) return;
    duplicateGem({ towerId, slotIndex: 0 });
  };
  recycleGemLate = () => {
    const towerId = inputAdapterRef?.getSelectedTowerId();
    if (!towerId) return;
    recycleGem({ towerId, slotIndex: 0 });
  };
  combineGemsLate = () => {
    inputAdapterRef?.triggerCombine();
  };
  // inventoryDragStartLate is wired after updateViewWithDrag below

  const exitToMap = createExitToMap({
    getSession,
    setSession,
    getState,
    render: renderAndHud,
    saveProgress,
    getMageProfile,
  });

  const gameLoop = createGameLoop({ tick });
  const handleExitToMap = (): void => {
    // If a story beat was queued by handleWin (e.g. G3 → hollow_one),
    // show it between the won-screen click and the actual map exit.
    if (pendingStoryBeat !== null) {
      const beat = pendingStoryBeat;
      pendingStoryBeat = null;
      gameLoop.stop();
      storyFinishFn = () => {
        exitToMap();
      };
      currentSceneOverride = "story";
      storyActions.showStory(beat);
      return;
    }
    exitToMap();
    gameLoop.stop();
  };

  function getMapView() {
    return mapStateToView(getState(), getMageProfile(), skillsPanelOpen, statsPanelOpen, campaign);
  }

  const learnSkill = createLearnSkill({
    getMageProfile,
    saveMageProfile: () => { saveProgress(); return Promise.resolve(); },
    render: renderAndHud,
    getMapView,
  });
  const refundSkill = createRefundSkill({
    getMageProfile,
    saveMageProfile: () => { saveProgress(); return Promise.resolve(); },
    render: renderAndHud,
    getMapView,
  });
  onSkillUpgradeLate = (skillId, options) => { learnSkill(skillId, options); };
  onSkillRefundLate = (skillId, options) => { refundSkill(skillId, options); };

  const toggleSkillsPanel = (): void => {
    skillsPanelOpen = !skillsPanelOpen;
    renderAndHud.update(getMapView());
  };

  const closeSkillsPanel = (): void => {
    if (skillsPanelOpen) {
      skillsPanelOpen = false;
      renderAndHud.update(getMapView());
    }
  };

  const toggleStatsPanel = (): void => {
    statsPanelOpen = !statsPanelOpen;
    renderAndHud.update(getMapView());
  };

  const closeStatsPanel = (): void => {
    if (statsPanelOpen) {
      statsPanelOpen = false;
      renderAndHud.update(getMapView());
    }
  };

  const goToMapButton = options.mountTarget.querySelector<HTMLButtonElement>(
    "#go-to-map-btn"
  );
  const localToggleBtn = options.mountTarget.querySelector<HTMLButtonElement>(
    "#locale-toggle-btn"
  );
  const audioToggleBtn = options.mountTarget.querySelector<HTMLButtonElement>(
    "#audio-toggle-btn"
  );
  const audioAdapter = audio as AudioPort & { setMuted?(v: boolean): void; isMuted?(): boolean };

  function updateI18nButtons(): void {
    if (goToMapButton) goToMapButton.textContent = t("map.worldMap");
    if (localToggleBtn) {
      // Show the label of the OTHER locale (so clicking switches to it)
      localToggleBtn.textContent = getLocale() === "ru" ? "EN" : "RU";
    }
    if (audioToggleBtn && audioAdapter.isMuted) {
      audioToggleBtn.textContent = audioAdapter.isMuted() ? "🔇" : "🔊";
    }
  }
  updateI18nButtons();
  if (localToggleBtn) {
    localToggleBtn.addEventListener("click", () => {
      setLocale(getLocale() === "ru" ? "en" : "ru");
      updateI18nButtons();
      const session = getSession();
      if (session) {
        // Force re-render so locale-sensitive HUD labels (gem effects etc.) update.
        const mode = inputAdapterRef?.getPlacementMode() ?? "Tower";
        renderAndHud.update(sessionToView(session, "game", null, mode, tutorialStep, campaign));
        return;
      }
      const s = getState();
      if (s.scene === "story" && s.story) {
        renderAndHud.update(storyToView(s, campaign));
        return;
      }
      if (s.scene === "map") {
        renderAndHud.update(getMapView());
      }
    });
  }
  const musicAdapter = music as MusicPort & { setMuted?(v: boolean): void; isMuted?(): boolean };
  if (audioToggleBtn && audioAdapter.setMuted && audioAdapter.isMuted) {
    audioToggleBtn.addEventListener("click", () => {
      const next = !audioAdapter.isMuted!();
      audioAdapter.setMuted!(next);
      musicAdapter.setMuted?.(next);
      updateI18nButtons();
    });
  }

  const updateViewWithDrag = (
    dragState: { gem: { id: string; type: string; level: number }; screenX: number; screenY: number } | null
  ) => {
    renderAndHud.setDragState?.(dragState);
    const s = getSession();
    if (!s) return;
    const mode = inputAdapterRef?.getPlacementMode() ?? "Tower";
    renderAndHud.update(sessionToView(s, "game", dragState, mode, tutorialStep, campaign));
  };

  // Wire inventory drag start now that inputAdapterRef is available.
  // We delegate to inputAdapter.startInventoryDrag so that dragSource is set
  // inside the input-adapter closure — document-level pointermove/up listeners
  // added in attach() will handle the continuation of the gesture.
  inventoryDragStartLate = (p) => {
    inputAdapterRef?.startInventoryDrag(p.gemId, p.screenX, p.screenY);
  };
  inventoryGemClickLate = (gemId: string) => {
    inputAdapterRef?.selectInventoryGem(gemId);
  };

  // Wire Phase 3 late-bound callbacks (use-cases are now available)
  handleExitToMapLate = handleExitToMap;
  onSkillUpgradeLate = (skillId: number) => { learnSkill(skillId); };
  closeSkillsPanelLate = closeSkillsPanel;
  toggleSkillsPanelLate = toggleSkillsPanel;
  closeStatsPanelLate = closeStatsPanel;
  toggleStatsPanelLate = toggleStatsPanel;
  openBattleSettingsLate = openBattleSettings;
  createGemLate = (gemType: string) => { createGem(gemType as import("./domain/types.js").GemType); };

  // Instantiate editor use-case now that renderAndHud and fieldConfig are available
  editorUCRef = createEditor({
    render: renderAndHud,
    fieldConfig,
    overrideStorage: fieldOverrideStorage,
    setScene: (sceneId) => {
      renderAndHud.setScene(sceneId as import("./domain/types.js").SceneId);
    },
    getMapStateView: () => getMapView(),
  });
  onHpChangeLate = (index: number) => {
    updateBattleSettings({ hpMultiplierOption: index as 0|1|2|3|4|5 });
  };
  onWavesChangeLate = (index: number) => {
    updateBattleSettings({ wavesOption: index as 0|1|2|3|4 });
  };
  onToggleChangeLate = (key: "extraBanishCost" | "healOnReturn") => {
    const draft = getDraftSettings();
    if (key === "extraBanishCost") {
      updateBattleSettings({ extraBanishCost: !draft.extraBanishCost });
    } else {
      updateBattleSettings({ healOnReturn: !draft.healOnReturn });
    }
  };
  onSettingsStartLate = () => {
    const result = confirmBattleSettings();
    if ("ok" in result && result.ok) {
      currentSceneOverride = null;
      gameLoop.start();
    }
  };
  onSettingsCancelLate = () => { cancelBattleSettings(); };

  // In-battle settings menu wiring.
  // Remember the player's chosen speed so "Resume" restores it (not always 1×).
  let speedBeforeMenu: import("./domain/session.js").SpeedMultiplier | null = null;
  onBattleMenuOpenLate = () => {
    const s = getSession();
    if (!s) return;
    speedBeforeMenu = s.speed === 0 ? 1 : s.speed;
    pause();
  };
  onBattleMenuCloseLate = () => {
    const restore = speedBeforeMenu ?? 1;
    speedBeforeMenu = null;
    _setSpeed(restore);
  };
  onBattleMenuToggleAudioLate = () => {
    if (!audioAdapter.setMuted || !audioAdapter.isMuted) return;
    const next = !audioAdapter.isMuted();
    audioAdapter.setMuted(next);
    musicAdapter.setMuted?.(next);
    updateI18nButtons();
  };
  onBattleMenuToggleLocaleLate = () => {
    setLocale(getLocale() === "ru" ? "en" : "ru");
    updateI18nButtons();
    // In battle, the game loop re-renders continuously; on map we need to nudge.
    if (!getSession()) renderAndHud.update(getMapView());
  };
  onBattleMenuRestartLate = () => {
    const s = getSession();
    if (!s) return;
    const fieldId = s.field.id;
    const settings = s.stageSettings;
    speedBeforeMenu = null;
    startBattle(fieldId, settings);
  };

  onEditorExportYamlLate = () => {
    const st = editorUCRef?.getEditorState();
    if (!st) {
      alert("Редактор не открыт");
      return;
    }
    const config = fieldConfig.getFieldConfig(st.fieldId as import("./domain/types.js").FieldId);
    if (!config) {
      alert("Не удалось получить конфиг поля");
      return;
    }
    const yamlText = buildFieldYaml(st, config);
    navigator.clipboard
      .writeText(yamlText)
      .then(() => alert(`YAML для ${st.fieldId} скопирован в буфер.\nВставь в src/campaign/sanctified/fields/${st.fieldId}.yml`))
      .catch((err) => {
        console.error("clipboard write failed", err);
        console.log(yamlText);
        alert("Буфер недоступен — YAML выведен в консоль");
      });
  };

  const inputAdapter = createInputAdapter({
    openBattleSettings,
    gameLoop,
    canvas,
    placeBuilding,
    getSession,
    getScene: () => {
      if (currentSceneOverride) return currentSceneOverride;
      return lastRenderedScene;
    },
    openEditor: (fieldId: string) => {
      currentSceneOverride = "editor";
      editorUCRef?.openEditor(fieldId);
    },
    editorHandleClick: (cell, mode) => {
      if (mode === "waypoint") {
        editorUCRef?.editorAddWaypoint(cell);
      } else {
        editorUCRef?.editorToggleBlocked(cell);
      }
    },
    editorSetHoverCell: (cell) => editorUCRef?.editorSetHoverCell(cell),
    editorSetMode: (mode) => editorUCRef?.editorSetMode(mode),
    editorRemoveLastWaypoint: () => editorUCRef?.editorRemoveLastWaypoint(),
    editorClearWaypoints: () => editorUCRef?.editorClearWaypoints(),
    editorCloseEditor: (save: boolean) => {
      currentSceneOverride = null;
      editorUCRef?.closeEditor(save);
    },
    getEditorMode: () => editorUCRef?.getEditorState()?.mode ?? null,
    getMapFieldIds: () => [...campaign.map.fieldIds],
    getMapGeometry: () => ({
      fieldIds: [...campaign.map.fieldIds],
      rows: campaign.map.rows,
      cols: campaign.map.cols,
      playableFieldIds: campaign.map.fieldIds.filter((fieldId) => campaign.map.isFieldPlayable(fieldId)),
    }),
    goToMapButton,
    onExitToMap: handleExitToMap,
    createGem,
    placeGem,
    removeGemFromTower,
    swapGemsBetweenTowers,
    updateViewWithDrag,
    upgradeGem,
    duplicateGem,
    combineGems,
    recycleGem,
    pause,
    resume,
    cycleSpeed,
    setSpeed,
    render: renderAndHud,
    onSkillButtonClick: (skillId: number) => { learnSkill(skillId); },
    onSkillsPanelToggle: toggleSkillsPanel,
    onSkillsPanelClose: closeSkillsPanel,
    getSkillsPanelOpen: () => skillsPanelOpen,
    onStatsPanelToggle: toggleStatsPanel,
    onStatsPanelClose: closeStatsPanel,
    getStatsPanelOpen: () => statsPanelOpen,
    onStageSettingSelectOption: (kind, index) => {
      if (kind === "hp") {
        updateBattleSettings({ hpMultiplierOption: index as 0|1|2|3|4|5 });
      } else {
        updateBattleSettings({ wavesOption: index as 0|1|2|3|4 });
      }
    },
    onStageSettingToggle: (kind) => {
      const draft = getDraftSettings();
      if (kind === "extraBanishCost") {
        updateBattleSettings({ extraBanishCost: !draft.extraBanishCost });
      } else {
        updateBattleSettings({ healOnReturn: !draft.healOnReturn });
      }
    },
    onStageSettingStart: () => {
      const result = confirmBattleSettings();
      if ("ok" in result && result.ok) {
        currentSceneOverride = null;
        gameLoop.start();
      }
    },
    onStageSettingCancel: () => { cancelBattleSettings(); },
    demolishTower: (towerId) => demolishTower(towerId),
    castGemBomb: (gemId, cell) => castGemBomb(gemId, cell),
    onTutorialNext: () => advanceTutorial("manual", renderAndHud, campaign),
    onTutorialSkip: () => dismissTutorial(renderAndHud, campaign),
    getTutorialStep: () => tutorialStep,
    onStoryNext: () => nextStoryPanel(),
    onStorySkip: () => storyActions.skipStory(),
  });

  inputAdapterRef = inputAdapter;

  let disposed = false;

  const beforeUnloadHandler = () => { composition.dispose(); };
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", beforeUnloadHandler);
  }

  const composition = {
    async run() {
      initTutorial(campaign);
      renderAndHud.setTutorialStep(tutorialStep);
      const result = await loadProgress();
      if (result.ok && result.state) {
        lastAppState = result.state;
        currentAppState = result.state;
        if (result.state.session) gameLoop.start();
        
        // Initial intro story
        const initialStoryBeat = campaign.story.initialBeatId;
        if (initialStoryBeat && !currentMapProgress.seenStoryBeats.has(initialStoryBeat)) {
          currentSceneOverride = "story";
          storyActions.showStory(initialStoryBeat);
        }
      }

      inputAdapter.attach?.();
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
      }
      gameLoop.dispose();
      inputAdapter.dispose();
      hud.unmount();
    },
    loadProgress,
    goToMap,
    startBattle,
    placeBuilding,
    createGem,
    placeGem,
    upgradeGem,
    duplicateGem,
    combineGems,
    recycleGem,
    tick,
    saveProgress,
    pause,
    resume,
    cycleSpeed,
    setSpeed,
    gameLoop,
    openEditor(fieldId: string): void {
      currentSceneOverride = "editor";
      editorUCRef?.openEditor(fieldId);
    },
    closeEditor(save: boolean): void {
      currentSceneOverride = null;
      editorUCRef?.closeEditor(save);
    },
  };

  return composition;
}
